/**
 * Conversation Store
 * Manages conversation CRUD operations
 */
import { create } from 'zustand'
import type { Conversation } from '../../lib/types'
import { conversationsApi } from '../../lib/api'
import { logger } from '@/lib/logger'

/**
 * Max fully-loaded conversations kept in the stale-while-revalidate cache. Bounds session
 * memory growth: the visible conversation lives in `currentConversation` independently, so
 * an evicted entry simply means revisiting it re-fetches (a normal cache miss).
 */
export const MAX_CACHED_CONVERSATIONS = 50

/**
 * Insert/update an entry in the loadedConversations cache as a bounded LRU: re-inserting
 * marks the key most-recently-used, and least-recently-used entries are evicted once the
 * cap is exceeded. Returns a NEW Map (immutable update for Zustand).
 */
function setCachedConversation(
  cache: Map<string, Conversation>,
  id: string,
  conversation: Conversation
): Map<string, Conversation> {
  const next = new Map(cache)
  // Delete-then-set re-inserts at the end → most-recently-used (Map preserves insertion order).
  next.delete(id)
  next.set(id, conversation)
  while (next.size > MAX_CACHED_CONVERSATIONS) {
    const lru = next.keys().next().value // oldest insertion = least-recently-used
    if (lru === undefined) break
    next.delete(lru)
  }
  return next
}

interface ConversationState {
  currentConversation: Conversation | null
  conversations: Conversation[]
  isLoading: boolean
  deletingConversationId: string | null
  /**
   * Client-side cache of fully-loaded conversations (with messages), keyed by id.
   * Powers stale-while-revalidate in selectConversation so revisiting a chat is
   * instant. CLEARED in reset() (cascaded from chatStore.reset() on login/logout)
   * to prevent any cross-user data leak.
   */
  loadedConversations: Map<string, Conversation>

  loadConversations: () => Promise<void>
  createConversation: (title: string) => Promise<Conversation | null>
  createTemporaryConversation: () => void
  selectConversation: (id: string) => Promise<void>
  renameConversation: (id: string, newTitle: string) => Promise<void>
  deleteConversation: (id: string) => Promise<void>
  setCurrentConversation: (conversation: Conversation | null) => void
  addConversation: (conversation: Conversation) => void
  updateConversationMessages: (updater: (conv: Conversation) => Conversation) => void
  reset: () => void
}

// Single-flight guard for loadConversations. On reload, bootstrap()'s prefetch
// and ChatPage's mount both call loadConversations concurrently; without dedup
// that's two GET /api/conversations for the same list. Stashing the in-flight
// promise collapses concurrent callers to one request (cleared in the finally,
// so a later call after completion re-fetches normally — stale-while-revalidate).
let _loadConversationsInFlight: Promise<void> | null = null

export const useConversationStore = create<ConversationState>((set, get) => ({
  currentConversation: null,
  conversations: [],
  isLoading: false,
  deletingConversationId: null,
  loadedConversations: new Map(),

  /**
   * Load all conversations for the user
   */
  loadConversations: async () => {
    if (_loadConversationsInFlight) return _loadConversationsInFlight

    _loadConversationsInFlight = (async () => {
      set({ isLoading: true })

      try {
        const response = await conversationsApi.list()

        if (response.success && response.data) {
          set({ conversations: response.data.conversations, isLoading: false })
        } else {
          throw new Error('Failed to load conversations')
        }
      } catch (error) {
        logger.error('Failed to load conversations', error)
        set({ isLoading: false })
      } finally {
        _loadConversationsInFlight = null
      }
    })()

    return _loadConversationsInFlight
  },

  /**
   * Create a new conversation
   * Returns the created conversation or null on failure
   */
  createConversation: async (title: string) => {
    set({ isLoading: true })

    try {
      const response = await conversationsApi.create(title)

      if (response.success && response.data) {
        const newConversation = response.data

        set(state => ({
          conversations: [newConversation, ...state.conversations],
          currentConversation: newConversation,
          isLoading: false
        }))

        return newConversation
      } else {
        throw new Error('Failed to create conversation')
      }
    } catch (error) {
      logger.error('Failed to create conversation', error)
      set({ isLoading: false })
      return null
    }
  },

  /**
   * Create a temporary conversation (ChatGPT-style)
   * This creates a local-only conversation that won't appear in the sidebar
   * until the first message is sent
   */
  createTemporaryConversation: () => {
    const tempConversation: Conversation = {
      id: `temp-${Date.now()}`,
      userId: 'temp',
      title: 'New Chat',
      messages: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    set({ currentConversation: tempConversation })
  },

  /**
   * Select and load a conversation
   */
  selectConversation: async (id: string) => {
    // Skip if this conversation is being deleted (prevents race condition)
    const { deletingConversationId, loadedConversations } = get()
    if (id === deletingConversationId) {
      return
    }

    const cached = loadedConversations.get(id)

    if (cached) {
      // Cache hit: render immediately with NO loading spinner, then revalidate
      // in the background (stale-while-revalidate).
      set({ currentConversation: cached, isLoading: false })

      // Fire-and-forget background refresh. Only apply the result if the user is
      // still viewing this conversation and it isn't being deleted.
      void (async () => {
        try {
          const response = await conversationsApi.get(id)
          if (!response.success || !response.data) return
          if (id === get().deletingConversationId) return

          const fresh = response.data
          set(state => {
            // Don't resurrect an entry that was deleted, reset away, or belongs to a
            // previous user (in-flight refresh resolving after delete/reset/logout):
            // only refresh the cache if this conversation is still relevant — present
            // in the list or as the current selection.
            const stillRelevant =
              state.currentConversation?.id === id ||
              state.conversations.some(c => c.id === id)
            if (!stillRelevant) {
              return {}
            }
            return {
              loadedConversations: setCachedConversation(state.loadedConversations, id, fresh),
              // Only swap the visible conversation if the user hasn't navigated away.
              currentConversation:
                state.currentConversation?.id === id ? fresh : state.currentConversation,
            }
          })
        } catch (error) {
          // Stale content is already shown; a failed background refresh is non-fatal.
          logger.error('Background conversation refresh failed', error)
        }
      })()

      return
    }

    // Cache miss: existing behavior — show loading, fetch, then cache the result.
    set({ isLoading: true })

    try {
      const response = await conversationsApi.get(id)

      // Check again after async call - conversation might have been deleted while fetching
      if (id === get().deletingConversationId) {
        set({ isLoading: false })
        return
      }

      if (response.success && response.data) {
        const loaded = response.data
        set(state => ({
          currentConversation: loaded,
          loadedConversations: setCachedConversation(state.loadedConversations, id, loaded),
          isLoading: false,
        }))
      } else {
        throw new Error('Failed to load conversation')
      }
    } catch (error) {
      // Don't show error if conversation was deleted during fetch
      if (id === get().deletingConversationId) {
        set({ isLoading: false })
        return
      }

      logger.error('Failed to load conversation', error)
      set({ isLoading: false })
    }
  },

  /**
   * Rename a conversation
   */
  renameConversation: async (id: string, newTitle: string) => {
    set({ isLoading: true })

    try {
      const response = await conversationsApi.update(id, newTitle)

      if (response.success && response.data) {
        set(state => {
          const nextCache = new Map(state.loadedConversations)
          const cachedConv = nextCache.get(id)
          if (cachedConv) {
            nextCache.set(id, { ...cachedConv, title: newTitle })
          }
          return {
            conversations: state.conversations.map(conv =>
              conv.id === id ? { ...conv, title: newTitle } : conv
            ),
            currentConversation: state.currentConversation?.id === id
              ? { ...state.currentConversation, title: newTitle }
              : state.currentConversation,
            loadedConversations: nextCache,
            isLoading: false
          }
        })
      } else {
        throw new Error('Failed to rename conversation')
      }
    } catch (error) {
      logger.error('Failed to rename conversation', error)
      set({ isLoading: false })
    }
  },

  /**
   * Delete a conversation
   */
  deleteConversation: async (id: string) => {
    const wasCurrentConversation = get().currentConversation?.id === id

    // Mark conversation as being deleted to prevent race conditions
    set({ isLoading: true, deletingConversationId: id })

    try {
      const response = await conversationsApi.delete(id)

      if (response.success) {
        // Create a new temporary conversation if we deleted the current one
        const tempConversation: Conversation | null = wasCurrentConversation ? {
          id: `temp-${Date.now()}`,
          userId: 'temp',
          title: 'New Chat',
          messages: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        } : null

        set(state => {
          const nextCache = new Map(state.loadedConversations)
          nextCache.delete(id)
          return {
            conversations: state.conversations.filter(c => c.id !== id),
            currentConversation: wasCurrentConversation
              ? tempConversation
              : state.currentConversation,
            loadedConversations: nextCache,
            isLoading: false,
            deletingConversationId: null
          }
        })
      } else {
        throw new Error('Failed to delete conversation')
      }
    } catch (error) {
      logger.error('Failed to delete conversation', error)
      set({
        isLoading: false,
        deletingConversationId: null
      })
    }
  },

  /**
   * Set current conversation directly
   */
  setCurrentConversation: (conversation: Conversation | null) => {
    set({ currentConversation: conversation })
  },

  /**
   * Add a conversation to the list
   */
  addConversation: (conversation: Conversation) => {
    set(state => ({
      conversations: [conversation, ...state.conversations]
    }))
  },

  /**
   * Update messages in current conversation
   */
  updateConversationMessages: (updater: (conv: Conversation) => Conversation) => {
    set(state => {
      if (!state.currentConversation) {
        return { currentConversation: null }
      }
      const updated = updater(state.currentConversation)
      // Don't cache ephemeral temp-* conversations: they would orphan in the cache
      // and trigger a 404 background refresh once promoted to a real persisted id.
      if (updated.id.startsWith('temp-')) {
        return { currentConversation: updated }
      }
      // Keep the cache in sync so revisiting this chat shows the freshest messages.
      return {
        currentConversation: updated,
        loadedConversations: setCachedConversation(state.loadedConversations, updated.id, updated),
      }
    })
  },

  /**
   * Reset store state (used on logout)
   */
  reset: () => {
    set({
      currentConversation: null,
      conversations: [],
      isLoading: false,
      deletingConversationId: null,
      // Clear the cache on logout/login to prevent cross-user data leak.
      loadedConversations: new Map()
    })
  }
}))
